"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { Box, Typography, Card, CardMedia, CircularProgress, IconButton } from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import RewardEditDialog from "../components/RewardEditDialog"
import Footer from "../components/Footer"
import { motion } from "framer-motion"
import { useAuth } from "../AuthContext"
import { db, functions } from "../config/firebase"
import { collection, getDocs } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"

// Images (WELLNESS)
import massageImg from "../assets/images/rewards-images/massageback.jpg"
import headSpaImg from "../assets/images/rewards-images/headspa.png"
import facialsImg from "../assets/images/rewards-images/facialsImg.jpg"
import bodyTreatmentImg from "../assets/images/rewards-images/blackrockonback.jpg"
import scrubImg from "../assets/images/rewards-images/scrub.webp"
import expressImg from "../assets/images/rewards-images/expressImg.jpg"
import bathSaunaImg from "../assets/images/rewards-images/lemonsonwater.jpg"
import agapePoolImg from "../assets/images/rewards-images/agapepool.jpg"

// Images (SUITES & VILLAS – reuse villa images or generic hotel images if you prefer)
import roomImg1 from "../assets/images/villa-images/image1.webp"
import roomImg2 from "../assets/images/villa-images/image2.webp"

// Images (PB PAVILION – reuse the same ones from PB Pavilion page)
import bougie from "../assets/images/PBPavillion-Images/bougie.png"
import uniliveonce from "../assets/images/PBPavillion-Images/uniliveonce.png"
import sushihouse from "../assets/images/PBPavillion-Images/sushihouse.png"
import inhomesushi from "../assets/images/PBPavillion-Images/inhomesushi.png"
import tunaspectacle from "../assets/images/PBPavillion-Images/tunaspectacle.png"
import sushiclass from "../assets/images/PBPavillion-Images/sushiclass.png"

const SOURCE_LABELS = {
  suite_booking: "Suite Booking",
  villa_booking: "Villa Booking",
  wellness_booking: "Wellness Center Booking",
  pavilion_booking: "PB Pavilion Booking",
  cart_booking: "Cart Checkout",
}

const TYPE_LABELS = {
  suite: "Suite",
  villa: "Villa",
  wellness: "Wellness",
  pavilion: "Pavilion",
}

const TYPE_ICONS = {
  suite: "🏨",
  villa: "🏡",
  wellness: "🌿",
  pavilion: "🍽️",
}

// Static fallback — used as initial state and merged with Firestore values.
// Icons are always sourced from here (not Firestore) per product decision.
const REWARD_DEFAULTS = [
  {
    id: "breakfast",
    name: "Complimentary Breakfast for Two",
    cost: 250,
    description: "Enjoy a full breakfast for two at our restaurant. Show the confirmation to staff at check-in.",
    icon: "🍳",
    imageUrl: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&q=80",
  },
  {
    id: "spa_credit",
    name: "Agape Spa Credit ($50 off)",
    cost: 500,
    description: "Redeem a $50 credit toward any Wellness Center service on your next visit.",
    icon: "💆",
    imageUrl: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&q=80",
  },
  {
    id: "room_upgrade",
    name: "Complimentary Room Upgrade",
    cost: 1000,
    description: "Upgrade your current booking to a higher suite tier at no extra cost, subject to availability.",
    icon: "🏨",
    imageUrl: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400&q=80",
  },
  {
    id: "wellness_session",
    name: "Complimentary Wellness Session",
    cost: 2500,
    description: "One full 60-minute wellness session of your choice at the Agape Wellness Center.",
    icon: "🌿",
    imageUrl: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&q=80",
  },
  {
    id: "free_night",
    name: "Free Night's Stay",
    cost: 5000,
    description: "One complimentary night in a select suite or villa, subject to availability.",
    icon: "🌙",
    imageUrl: "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=400&q=80",
  },
  {
    id: "luxury_package",
    name: "Luxury Experience Package",
    cost: 10000,
    description: "The full Agape experience: one night's stay, a Wellness session, and a PB Pavilion dinner for two.",
    icon: "✨",
    imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80",
  },
]

// WELLNESS – earning bubbles
const wellnessEarningRewards = [
  {
    name: "Massages",
    img: massageImg,
    points: 150,
    description: "Earn 150 points when you book any massage service.",
  },
  {
    name: "Head Spa",
    img: headSpaImg,
    points: 150,
    description: "Earn 150 points for each head spa session.",
  },
  {
    name: "Facials",
    img: facialsImg,
    points: 150,
    description: "Earn 150 points for each facial treatment.",
  },
  {
    name: "Body Treatments",
    img: bodyTreatmentImg,
    points: 250,
    description: "Earn 250 points for premium body treatment experiences.",
  },
  {
    name: "Scrubs",
    img: scrubImg,
    points: 250,
    description: "Earn 250 points for every full-body scrub reservation.",
  },
  {
    name: "Express Treatments",
    img: expressImg,
    points: 300,
    description: "Earn 300 points for express wellness add-ons.",
  },
  {
    name: "Bath/Sauna Rental",
    img: bathSaunaImg,
    points: 400,
    description: "Earn 400 points for bath and sauna private rentals.",
  },
  {
    name: "Agape Pool Experience",
    img: agapePoolImg,
    points: 500,
    description: "Earn 500 points for the Agape Pool Experience.",
  },
]

// SUITES & VILLAS EARNING (formerly Hotel)
const hotelEarningRewards = [
  {
    name: "Suite Stay",
    img: roomImg1,
    points: 250,
    description: "Earn 250 points per night when you book any suite.",
  },
  {
    name: "Villa Stay",
    img: roomImg2,
    points: 200,
    description: "Earn 200 points per night when you book any villa.",
  },
]

// PB PAVILION EARNING (one bubble per experience)
const pavilionEarningRewards = [
  {
    name: "Legacy Lounge: Baldwin & Bubbles",
    img: inhomesushi,
    points: 300,
    description: "Earn 300 points per guest when you reserve this experience.",
  },
  {
    name: "Civic Spirit: Hamilton Pour",
    img: sushihouse,
    points: 320,
    description: "Earn 320 points per guest for this cocktail-forward lounge tasting.",
  },
  {
    name: "Island Revival: Garífuna Gold",
    img: uniliveonce,
    points: 350,
    description: "Earn 350 points per guest for this Afro-Caribbean tasting journey.",
  },
  {
    name: "Prime Roots: Agape Garden Table",
    img: tunaspectacle,
    points: 360,
    description: "Earn 360 points per guest for this garden-to-glass experience.",
  },
  {
    name: "The American Dream: Tubman",
    img: bougie,
    points: 380,
    description: "Earn 380 points per guest for this premium long-form dining event.",
  },
  {
    name: "Liberation Fire: Maroon Feast",
    img: sushiclass,
    points: 400,
    description: "Earn 400 points per guest for this live-fire communal feast.",
  },
]

export default function Points() {
  const { currentUser, authReady, isAdmin } = useAuth()

  const [points, setPoints]       = useState(0)
  const [totalEarned, setTotalEarned] = useState(0)
  const [history, setHistory]     = useState([])
  const [loading, setLoading]     = useState(true)

  const [confirmReward, setConfirmReward] = useState(null)
  const [redeeming, setRedeeming]         = useState(false)
  const [redeemMsg, setRedeemMsg]         = useState(null)
  const [expandedCart, setExpandedCart]   = useState(new Set())
  const [editingReward, setEditingReward] = useState(null)

  // Rewards listings — seeded from hardcoded defaults, overridden by Firestore when available
  const [redeemableRewards, setRedeemableRewards] = useState(REWARD_DEFAULTS)

  // Balance + history now live in Agape's Firestore (the shared, authoritative
  // rewards account) — fetched via a Cloud Function rather than direct local
  // Firestore reads, since the browser has no way to reach Agape's project
  // directly. See functions/sharedRewardsClient.js on the backend.
  const fetchSharedBalance = async () => {
    const getSharedRewardsBalance = httpsCallable(functions, "getSharedRewardsBalance")
    const result = await getSharedRewardsBalance()
    const data = result.data || {}
    setPoints(data.currentPoints || 0)
    setTotalEarned(data.totalEarned || 0)
    setHistory(Array.isArray(data.history) ? data.history : [])
  }

  useEffect(() => {
    if (!authReady) return
    if (!currentUser) { setLoading(false); return }

    const fetchPoints = async () => {
      try {
        await fetchSharedBalance()
      } catch (err) {
        console.error("Error fetching points:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchPoints()
  }, [authReady, currentUser])

  const refreshHistory = async () => {
    await fetchSharedBalance()
  }

  // Load reward definitions from Firestore, merging over hardcoded defaults.
  // Runs once on mount; public read so not gated on auth.
  useEffect(() => {
    const fetchRewards = async () => {
      try {
        const snap = await getDocs(collection(db, "redeemableRewards"))
        if (snap.empty) return
        const fsMap = {}
        snap.docs.forEach((d) => { fsMap[d.id] = d.data() })
        setRedeemableRewards(
          REWARD_DEFAULTS.map((def) => {
            const fs = fsMap[def.id]
            if (!fs) return def
            return {
              ...def, // preserves icon
              name:        fs.name        ?? def.name,
              description: fs.description ?? def.description,
              cost:        typeof fs.cost === "number" ? fs.cost : def.cost,
              imageUrl:    fs.imageUrl    ?? def.imageUrl,
            }
          })
        )
      } catch (err) {
        console.error("Failed to load redeemable rewards from Firestore:", err)
        // keep REWARD_DEFAULTS as fallback — state is already initialised with them
      }
    }
    fetchRewards()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Redemption is now a server-side call against Agape's authoritative shared
  // balance (redeemSharedReward Cloud Function) rather than a client-side
  // Firestore transaction against a local doc — the frontend never has
  // privileged write access to the balance itself, only the ability to
  // request a redemption through an authenticated Cloud Function call.
  const handleRedeem = async (reward) => {
    if (!currentUser || points < reward.cost || redeeming) return
    setRedeeming(true)
    setRedeemMsg(null)
    try {
      const redeemSharedReward = httpsCallable(functions, "redeemSharedReward")
      const result = await redeemSharedReward({ points: reward.cost, rewardName: reward.name })
      setPoints(result.data?.newBalance ?? points - reward.cost)
      setConfirmReward(null)
      setRedeemMsg({ type: "success", reward })
      await refreshHistory()
    } catch (err) {
      setRedeemMsg({ type: "error", text: err.message || "Redemption failed. Please try again." })
    } finally {
      setRedeeming(false)
    }
  }

  const toggleCartExpand = (id) => {
    setExpandedCart((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const maxPoints = 10000
  const progressPercentage = (points / maxPoints) * 100
  const circumference = 2 * Math.PI * 90 // radius of 90px
  const strokeDashoffset = circumference - (progressPercentage / 100) * circumference

  // Shared section heading styles (smaller version of FAQ headings)
  const sectionHeadingSx = {
    fontFamily: "'Satoshi', 'SF Pro Display', 'Inter', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "1.35rem",
    letterSpacing: "-0.02em",
    color: "#f9fafb",
    textAlign: "left",
    mb: 0.5,
    mt: 3,
    textShadow: "0 1px 6px rgba(0,0,0,0.4)",
  }

  const sectionAccentSx = {
    width: 60,
    height: 3,
    background: "linear-gradient(90deg, #002147 0%, #005792 100%)",
    borderRadius: "2px",
    mb: 2,
    opacity: 0.85,
  }

  const cardBubbleSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px",
    borderRadius: "15px",
    backgroundColor: "rgba(255,255,255,0.15)",
    backdropFilter: "blur(6px)",
    color: "#fff",
  }

  const pillSx = {
    backgroundColor: "rgba(15, 23, 42, 0.9)",
    borderRadius: "999px",
    px: 2,
    py: 0.5,
    fontSize: "0.8rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  }

  return (
    <>
      <div style={{ position: "relative", overflow: "hidden", fontFamily: "'Inter', sans-serif" }}>
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            minWidth: "100%",
            minHeight: "100%",
            objectFit: "cover",
            zIndex: -1,
          }}
        >
          <source src="/videos/PavilionVid.mp4" type="video/mp4" />
        </video>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            minHeight: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            paddingTop: "80px",
            paddingBottom: "60px",
          }}
        >
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              borderRadius: "20px",
              padding: "40px 30px",
              width: "90%",
              maxWidth: "900px",
              color: "white",
              textAlign: "center",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            }}
          >
            {/* HEADER */}
            <Box sx={{ marginBottom: "30px" }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  color: "#ffffff",
                  letterSpacing: "-0.02em",
                  textShadow: "0 2px 18px #fff6",
                  marginBottom: "8px",
                  fontSize: {
                    xs: "2.5rem",
                    md: "3.5rem",
                  },
                }}
              >
                Rewards
              </Typography>

              <Box
                sx={{
                  width: "80px",
                  height: "3px",
                  background: "linear-gradient(90deg, transparent, #3b82f6, transparent)",
                  margin: "0 auto 16px",
                  borderRadius: "999px",
                  boxShadow: "0 0 10px rgba(59, 130, 246, 0.5)",
                }}
              />

              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: 500,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                  lineHeight: 1.6,
                }}
              >
                Earn points and unlock{" "}
                <a
                  href="/faq#rewardsAccordion"
                  style={{
                    color: "#fff",
                    textDecoration: "underline",
                    fontWeight: 600,
                  }}
                >
                  rewards
                </a>
                .{" "}
                <span
                  style={{
                    color: "#60a5fa",
                    fontWeight: 700,
                    textShadow: "0 0 10px rgba(96, 165, 250, 0.4)",
                  }}
                >
                  10,000 points
                </span>{" "}
                = free luxury experience.
              </Typography>
            </Box>

            {/* SAME-EMAIL NOTICE — rewards are shared with Agape Prime Hotel via email match */}
            <Box
              sx={{
                py: 1.5, px: 2.5, mb: 3,
                background: "rgba(59,130,246,0.08)",
                borderRadius: "10px",
                border: "1px solid rgba(59,130,246,0.25)",
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85rem", lineHeight: 1.5 }}>
                Rewards are shared with Agape Prime Hotel. Use the <strong>same email address</strong> on
                both applications so your points stay linked to one balance.
              </Typography>
            </Box>

            {/* POINTS BALANCE — loading / auth gate / live data */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: "#3b82f6" }} />
              </Box>
            ) : !currentUser ? (
              <Box
                sx={{
                  py: 5, px: 3, mb: 4,
                  background: "rgba(255,255,255,0.07)",
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.15)",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.75)", mb: 2, fontSize: "1rem" }}>
                  Sign in to view your points balance and rewards history.
                </Typography>
                <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                  <Link to="/sign-in" style={{
                    padding: "10px 28px", background: "#0f172ae6", color: "#fff",
                    borderRadius: "8px", fontWeight: 700, textDecoration: "none", fontSize: "0.95rem",
                  }}>Sign In</Link>
                  <Link to="/sign-up" style={{
                    padding: "10px 28px", background: "rgba(255,255,255,0.12)", color: "#fff",
                    borderRadius: "8px", fontWeight: 600, textDecoration: "none", fontSize: "0.95rem",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}>Create Account</Link>
                </Box>
              </Box>
            ) : (
              <>
                {/* CIRCULAR PROGRESS */}
                <Box sx={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
                  <Box sx={{ position: "relative", width: "200px", height: "200px" }}>
                    <svg width="200" height="200" style={{ transform: "rotate(-90deg)" }}>
                      <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255, 255, 255, 0.2)" strokeWidth="12" />
                      <circle
                        cx="100" cy="100" r="90" fill="none"
                        stroke="#3b82f6" strokeWidth="12"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        style={{ transition: "stroke-dashoffset 0.5s ease-in-out" }}
                      />
                    </svg>
                    <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
                        {points.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#eee", marginTop: "4px" }}>
                        of {maxPoints.toLocaleString()} pts
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {totalEarned > 0 && (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", mb: 3, fontSize: "0.82rem" }}>
                    {totalEarned.toLocaleString()} pts earned all-time
                  </Typography>
                )}

                {/* LINEAR PROGRESS */}
                <Box sx={{ marginBottom: "40px" }}>
                  <Typography variant="body2" sx={{ marginBottom: "12px", color: "#eee" }}>
                    Next luxury experience at {maxPoints.toLocaleString()} points
                  </Typography>
                  <Box sx={{ position: "relative", height: "32px" }}>
                    <div style={{
                      width: "100%", height: "12px",
                      backgroundColor: "rgba(255,255,255,0.25)",
                      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      borderRadius: "999px", overflow: "hidden",
                    }}>
                      <div style={{
                        height: "100%", width: `${progressPercentage}%`,
                        background: "linear-gradient(90deg, #3b82f6, #1d4ed8)",
                        transition: "width 0.5s ease-in-out",
                      }} />
                    </div>
                    {[0, 2500, 5000, 7500, 10000].map((value, i) => (
                      <span key={i} style={{
                        position: "absolute", left: `${(value / maxPoints) * 100}%`,
                        transform: "translateX(-50%)", top: "20px",
                        color: "#fff", fontSize: "12px", fontWeight: 500,
                      }}>
                        {value.toLocaleString()}
                      </span>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* POINTS HISTORY */}
            {currentUser && !loading && history.length > 0 && (
              <Box sx={{ textAlign: "left", mb: 4 }}>
                <Typography sx={sectionHeadingSx}>Points History</Typography>
                <Box sx={sectionAccentSx} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {history.map((tx) => {
                    const isEarned = tx.type === "earned"
                    const label = tx.type === "redeemed" && tx.rewardName
                      ? tx.rewardName
                      : SOURCE_LABELS[tx.source] || tx.source || "Transaction"
                    const dateStr = tx.timestamp?.toDate
                      ? tx.timestamp.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"

                    // Cart checkout: collapsible card with per-item breakdown
                    if (tx.source === "cart_booking") {
                      const hasItems = Array.isArray(tx.items) && tx.items.length > 0
                      const isExpanded = expandedCart.has(tx.id)
                      const itemCount = hasItems ? tx.items.length : 0
                      return (
                        <Box key={tx.id} sx={{
                          background: "rgba(255,255,255,0.14)", borderRadius: "12px",
                          border: isExpanded ? "1px solid rgba(96,165,250,0.45)" : "1px solid rgba(255,255,255,0.22)",
                          overflow: "hidden", transition: "border-color 0.2s",
                        }}>
                          {/* Clickable header row */}
                          <Box
                            onClick={() => toggleCartExpand(tx.id)}
                            sx={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "12px 16px", cursor: "pointer",
                              "&:hover": { background: "rgba(255,255,255,0.05)" },
                              transition: "background 0.15s",
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Box sx={{
                                width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                background: "rgba(59,130,246,0.28)", border: "1px solid rgba(59,130,246,0.55)",
                                fontSize: "16px",
                              }}>
                                ⭐
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#fff" }}>
                                    {label}
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.62rem", color: "#60a5fa", fontWeight: 700, lineHeight: 1 }}>
                                    {isExpanded ? "▲" : "▼"}
                                  </Typography>
                                </Box>
                                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.72)", mt: 0.25 }}>
                                  {dateStr} · Balance after: {tx.newBalance?.toLocaleString() ?? "—"} pts
                                  {itemCount > 0 && (
                                    <Typography component="span" sx={{ fontSize: "0.65rem", color: "rgba(96,165,250,0.72)", ml: 0.75 }}>
                                      · {itemCount} item{itemCount === 1 ? "" : "s"}
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#93c5fd", whiteSpace: "nowrap", ml: 1.5 }}>
                              +{Math.abs(tx.points).toLocaleString()} pts
                            </Typography>
                          </Box>

                          {/* Expanded item list */}
                          {isExpanded && (
                            <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.12)", px: 1.5, pb: 1.5, pt: 1.25 }}>
                              {hasItems ? (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                  {tx.items.map((item, idx) => (
                                    <Box key={idx} sx={{
                                      display: "flex", alignItems: "center", justifyContent: "space-between",
                                      background: "rgba(0,0,0,0.22)", borderRadius: "9px",
                                      padding: "9px 12px", gap: 1.5,
                                    }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: "17px", lineHeight: 1, flexShrink: 0 }}>
                                          {TYPE_ICONS[item.type] || "📋"}
                                        </Typography>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>
                                            {item.name}
                                          </Typography>
                                          <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.45)", mt: "1px" }}>
                                            {[TYPE_LABELS[item.type], item.detail].filter(Boolean).join(" · ")}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      {item.points != null && (
                                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#93c5fd", whiteSpace: "nowrap" }}>
                                          +{item.points.toLocaleString()} pts
                                        </Typography>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", fontStyle: "italic", px: 0.5 }}>
                                  No item details available.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      )
                    }

                    // All other entries: original flat layout
                    return (
                      <Box key={tx.id} sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: "rgba(255,255,255,0.14)", borderRadius: "12px",
                        padding: "12px 16px", border: "1px solid rgba(255,255,255,0.22)",
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: isEarned ? "rgba(59,130,246,0.28)" : "rgba(239,68,68,0.28)",
                            border: `1px solid ${isEarned ? "rgba(59,130,246,0.55)" : "rgba(239,68,68,0.55)"}`,
                            fontSize: "16px",
                          }}>
                            {isEarned ? "⭐" : "🎁"}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#fff" }}>
                              {label}
                            </Typography>
                            {tx.items && tx.items.length > 0 && (
                              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.52)", mt: 0.2, lineHeight: 1.45 }}>
                                {tx.items.map((item) => item.detail ? `${item.name} (${item.detail})` : item.name).join(" · ")}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.72)", mt: tx.items?.length ? 0.3 : 0 }}>
                              {dateStr} · Balance after: {tx.newBalance?.toLocaleString() ?? "—"} pts
                            </Typography>
                          </Box>
                        </Box>
                        <Typography sx={{
                          fontWeight: 700, fontSize: "0.95rem", whiteSpace: "nowrap",
                          color: isEarned ? "#93c5fd" : "#fca5a5", ml: 1.5,
                        }}>
                          {isEarned ? "+" : "−"}{Math.abs(tx.points).toLocaleString()} pts
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}

            {/* REDEEM REWARDS */}
            {currentUser && !loading && (
              <Box sx={{ textAlign: "left", mb: 4 }}>
                <Typography sx={sectionHeadingSx}>Redeem Rewards</Typography>
                <Box sx={sectionAccentSx} />

                {redeemMsg?.type === "success" && (
                  <Box sx={{
                    mb: 2, p: 2, borderRadius: "12px",
                    background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
                  }}>
                    <Typography sx={{ fontWeight: 700, color: "#4ade80", fontSize: "0.95rem" }}>
                      🎉 {redeemMsg.reward.name} redeemed!
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.82rem", mt: 0.5 }}>
                      Show this page to our staff at check-in or upon arrival to claim your reward.
                      Your new balance is <strong style={{ color: "#fff" }}>{points.toLocaleString()} pts</strong>.
                    </Typography>
                  </Box>
                )}

                {redeemMsg?.type === "error" && (
                  <Box sx={{
                    mb: 2, p: 2, borderRadius: "12px",
                    background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
                  }}>
                    <Typography sx={{ color: "#f87171", fontSize: "0.88rem" }}>{redeemMsg.text}</Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {redeemableRewards.map((reward) => {
                    const canAfford = points >= reward.cost
                    return (
                      <Box key={reward.id} sx={{ position: "relative" }}>
                        {isAdmin && (
                          <IconButton
                            onClick={(e) => { e.stopPropagation(); setEditingReward(reward); }}
                            size="small"
                            sx={{
                              position: "absolute", top: 8, left: 8, zIndex: 10,
                              backgroundColor: "rgba(255,255,255,0.92)", width: 30, height: 30,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                              "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
                          </IconButton>
                        )}
                        <Box sx={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: "rgba(255,255,255,0.15)",
                        backdropFilter: "blur(6px)",
                        borderRadius: "15px", padding: "12px 16px",
                        border: canAfford
                          ? "1px solid rgba(255,255,255,0.35)"
                          : "1px solid rgba(255,255,255,0.15)",
                        boxShadow: canAfford
                          ? "0 0 18px rgba(255,255,255,0.12), 0 4px 16px rgba(0,0,0,0.2)"
                          : "0 2px 8px rgba(0,0,0,0.15)",
                        gap: 2,
                        color: "#fff",
                      }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                          <Box sx={{
                            width: 44, height: 44, borderRadius: "10px", flexShrink: 0,
                            overflow: "hidden",
                            ...(reward.imageUrl ? {} : {
                              display: "flex", alignItems: "center", justifyContent: "center",
                              background: "rgba(255,255,255,0.1)",
                              fontSize: "22px",
                            }),
                          }}>
                            {reward.imageUrl ? (
                              <img
                                src={reward.imageUrl}
                                alt={reward.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                              />
                            ) : reward.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#fff" }}>
                              {reward.name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
                              {reward.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75, flexShrink: 0 }}>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff", whiteSpace: "nowrap" }}>
                            {reward.cost.toLocaleString()} pts
                          </Typography>
                          <button
                            onClick={() => { if (canAfford) { setConfirmReward(reward); setRedeemMsg(null) } }}
                            disabled={!canAfford}
                            style={{
                              backgroundColor: "rgba(15, 23, 42, 0.9)",
                              borderRadius: "999px",
                              padding: "4px 16px",
                              fontWeight: 600,
                              fontSize: "0.78rem",
                              whiteSpace: "nowrap",
                              color: "#fff",
                              border: "none",
                              cursor: canAfford ? "pointer" : "default",
                              boxShadow: canAfford ? "0 0 10px rgba(255,255,255,0.18)" : "none",
                            }}
                          >
                            {canAfford ? "Redeem" : `Need ${(reward.cost - points).toLocaleString()} more pts`}
                          </button>
                        </Box>
                        </Box>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}

            {/* SUITES & VILLAS SECTION (EARN) - moved to top */}
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={sectionHeadingSx}>Suites & Villas</Typography>
              <Box sx={sectionAccentSx} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 3 }}>
              {hotelEarningRewards.map((card, index) => (
                <motion.div key={index} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card sx={cardBubbleSx}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CardMedia component="img" image={card.img} alt={card.name}
                        sx={{ width: 60, height: 60, borderRadius: "10px", marginRight: 2, objectFit: "cover" }} />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{card.name}</Typography>
                        <Typography variant="caption">{card.description}</Typography>
                      </Box>
                    </Box>
                    <Box sx={pillSx}>+{card.points} pts</Box>
                  </Card>
                </motion.div>
              ))}
            </Box>

            {/* WELLNESS CENTER SECTION (EARN) - now in middle */}
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={sectionHeadingSx}>Wellness Center</Typography>
              <Box sx={sectionAccentSx} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2, mb: 3 }}>
              {wellnessEarningRewards.map((card, index) => (
                <motion.div key={index} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card sx={cardBubbleSx}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CardMedia component="img" image={card.img} alt={card.name}
                        sx={{ width: 60, height: 60, borderRadius: "10px", marginRight: 2, objectFit: "cover" }} />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{card.name}</Typography>
                        <Typography variant="caption">{card.description}</Typography>
                      </Box>
                    </Box>
                    <Box sx={pillSx}>+{card.points} pts</Box>
                  </Card>
                </motion.div>
              ))}
            </Box>

            {/* PB PAVILION SECTION (EARN) */}
            <Box sx={{ textAlign: "left" }}>
              <Typography sx={sectionHeadingSx}>PB Pavilion</Typography>
              <Box sx={sectionAccentSx} />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
              {pavilionEarningRewards.map((card, index) => (
                <motion.div key={index} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card sx={cardBubbleSx}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CardMedia component="img" image={card.img} alt={card.name}
                        sx={{ width: 60, height: 60, borderRadius: "10px", marginRight: 2, objectFit: "cover" }} />
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{card.name}</Typography>
                        <Typography variant="caption">{card.description}</Typography>
                      </Box>
                    </Box>
                    <Box sx={pillSx}>+{card.points} pts</Box>
                  </Card>
                </motion.div>
              ))}
            </Box>
          </Box>
        </Box>
      </div>

      {/* CONFIRMATION MODAL */}
      {confirmReward && (
        <Box sx={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", p: 2,
        }}
          onClick={(e) => { if (e.target === e.currentTarget && !redeeming) setConfirmReward(null) }}
        >
          <Box sx={{
            background: "linear-gradient(160deg,#0a1628 0%,#0d1b4c 100%)",
            borderRadius: "18px", p: 4, maxWidth: "420px", width: "100%",
            border: "1px solid rgba(59,130,246,0.3)",
            boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            textAlign: "center",
          }}>
            {confirmReward.imageUrl ? (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <img
                  src={confirmReward.imageUrl}
                  alt={confirmReward.name}
                  style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 10 }}
                />
              </Box>
            ) : (
              <Typography sx={{ fontSize: "2rem", mb: 1 }}>{confirmReward.icon}</Typography>
            )}
            <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#fff", mb: 1 }}>
              Confirm Redemption
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem", mb: 0.5 }}>
              {confirmReward.name}
            </Typography>
            <Typography sx={{ color: "#60a5fa", fontWeight: 700, fontSize: "1.1rem", mb: 2 }}>
              {confirmReward.cost.toLocaleString()} pts
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", mb: 3 }}>
              Your balance after: <strong style={{ color: "#fff" }}>{(points - confirmReward.cost).toLocaleString()} pts</strong>
            </Typography>

            {redeemMsg?.type === "error" && (
              <Typography sx={{ color: "#f87171", fontSize: "0.82rem", mb: 2 }}>{redeemMsg.text}</Typography>
            )}

            <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
              <button
                onClick={() => !redeeming && setConfirmReward(null)}
                disabled={redeeming}
                style={{
                  padding: "10px 24px", borderRadius: "8px", fontWeight: 600, fontSize: "0.88rem",
                  background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)",
                  border: "1px solid rgba(255,255,255,0.15)", cursor: redeeming ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRedeem(confirmReward)}
                disabled={redeeming}
                style={{
                  padding: "10px 28px", borderRadius: "8px", fontWeight: 700, fontSize: "0.88rem",
                  background: redeeming ? "rgba(59,130,246,0.4)" : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                  color: "#fff", border: "none", cursor: redeeming ? "not-allowed" : "pointer",
                  minWidth: "120px",
                }}
              >
                {redeeming ? "Redeeming…" : "Confirm"}
              </button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Reward edit dialog — admin only, opened by pencil icon on each redeemable reward card */}
      <RewardEditDialog
        reward={editingReward}
        onClose={() => setEditingReward(null)}
        onSaved={(updates) => {
          setRedeemableRewards((prev) =>
            prev.map((r) => r.id === updates.id ? { ...r, ...updates } : r)
          );
        }}
      />

      <Footer />
    </>
  )
}
